// // src/App.js
// import DonationDashboard from "./components/DonationDashboard";
// import AidReceiptTable from "./components/GECAidReceiptTable";
// import ProcurementPage from "./components/HRCDistributed";
// import MedicalDonationsPage from "./components/MOPHDistributed";

// function App() {
//   // return <DonationDashboard />;
//   // return <AidReceiptTable />;
//   // return <ProcurementPage />;
//   return <MedicalDonationsPage />;
// }
// export default App;

import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ThemeProvider, createTheme, CssBaseline, Container, Box, Toolbar } from "@mui/material";
import Navigation from "./components/Navigation";
import DonationDashboard from "./components/DonationDashboard";
import GECAidReceiptTable from './components/GECAidReceiptTable';
import HRCDistributed from './components/HRCDistributed';
import MOPHDistributed from './components/MOPHDistributed';
import SouthCouncil from './components/SouthCouncil';
import AidAnalysis from './components/AidAnalysis';
import AgencyStats from './components/AgencyStats';
import KeyTermsFAQ from './components/KeyTermsFQA';
import AidChatbot from './components/AidChatbot';
import Footer from "./components/Footer";
import { LanguageProvider } from './contexts/LanguageContext';

import "./styles/globals.css";
import './i18n';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0c9fc1', // Main brand color - teal/cyan
      light: '#4db6d1',
      dark: '#0a7a96',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#0d7377', // Complementary darker teal
      light: '#14a085',
      dark: '#0a5d61',
      contrastText: '#ffffff',
    },
    success: {
      main: '#38a169', // Professional green
      light: '#68d391',
      dark: '#2f855a',
    },
    warning: {
      main: '#f6ad55', // Warm orange that complements teal
      light: '#fbd38d',
      dark: '#ed8936',
    },
    error: {
      main: '#e53e3e', // Professional red
      light: '#fc8181',
      dark: '#c53030',
    },
    info: {
      main: '#0c9fc1', // Match primary for consistency
      light: '#4db6d1',
      dark: '#0a7a96',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#2d3748',
      secondary: '#4a5568',
    },
    grey: {
      50: '#f7fafc',
      100: '#edf2f7',
      200: '#e2e8f0',
      300: '#cbd5e0',
      400: '#a0aec0',
      500: '#718096',
      600: '#4a5568',
      700: '#2d3748',
      800: '#1a202c',
      900: '#171923',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Playfair Display", serif',
      fontWeight: 700,
      fontSize: '3.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: '"Playfair Display", serif',
      fontWeight: 600,
      fontSize: '2.75rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      fontSize: '2.25rem',
      lineHeight: 1.4,
    },
    h4: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      fontSize: '1.875rem',
      lineHeight: 1.4,
    },
    h5: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.5,
    },
    h6: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.5,
    },
    body1: {
      fontFamily: '"Source Sans Pro", sans-serif',
      fontSize: '1rem',
      lineHeight: 1.6,
      color: '#4a5568',
    },
    body2: {
      fontFamily: '"Source Sans Pro", sans-serif',
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: '#4a5568',
    },
    button: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.02em',
    },
  },
  shape: {
    borderRadius: 16,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0, 0, 0, 0.05)',
    '0px 4px 8px rgba(0, 0, 0, 0.08)',
    '0px 8px 16px rgba(0, 0, 0, 0.1)',
    '0px 12px 24px rgba(0, 0, 0, 0.12)',
    '0px 16px 32px rgba(0, 0, 0, 0.15)',
    '0px 20px 40px rgba(0, 0, 0, 0.18)',
    '0px 24px 48px rgba(0, 0, 0, 0.2)',
    '0px 32px 64px rgba(0, 0, 0, 0.25)',
    ...Array(16).fill('0px 32px 64px rgba(0, 0, 0, 0.25)'),
  ],
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '12px 24px',
          fontSize: '0.95rem',
          fontWeight: 600,
          textTransform: 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
          boxShadow: '0px 4px 15px rgba(12, 159, 193, 0.4)',
          '&:hover': {
            background: 'linear-gradient(135deg, #0a7a96 0%, #0c9fc1 100%)',
            boxShadow: '0px 8px 25px rgba(12, 159, 193, 0.6)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            transition: 'all 0.3s ease',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#0c9fc1',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#0c9fc1',
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0c9fc1',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0c9fc1',
            borderWidth: 2,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
          boxShadow: '0px 4px 20px rgba(12, 159, 193, 0.3)',
        },
      },
    },
  },
});

function App() {
  return (
    <LanguageProvider baseTheme={theme}>
     
      <CssBaseline />
      <Router>
        <Navigation />
        <Toolbar /> {/* This creates space for the fixed AppBar */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            minHeight: "calc(100vh - 64px)", // Adjusted for fixed AppBar
            pb: 6,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at 20% 80%, rgba(12, 159, 193, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(77, 182, 209, 0.1) 0%, transparent 50%)',
              pointerEvents: 'none',
            },
          }}
        >
          <Container maxWidth="xl" sx={{ pt: 4, position: 'relative', zIndex: 1 }}>
            <Routes>
              <Route path="/" element={<DonationDashboard />} />
              <Route path="/gec" element={<GECAidReceiptTable />} />
              <Route path="/hrc" element={<HRCDistributed />} />
              <Route path="/moph" element={<MOPHDistributed />} />
              <Route path="/south" element={<SouthCouncil />} />
              <Route path="/analysis" element={<AidAnalysis />} />
              <Route path="/stats/:agency" element={<AgencyStats />} />
              <Route path="/faq" element={<KeyTermsFAQ />} />
            </Routes>
          </Container>
        </Box>
        <AidChatbot />
        <Footer />
      </Router>
    </LanguageProvider>
  );
}

export default App;
